/**
 * RecentEventsSection — renders Datadog Events API output for the issue window.
 *
 * Engineer-readable timeline: source chip + when-ago + title + collapsible
 * full event text. The "what changed in the 60 minutes before this error?"
 * answer.
 */

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  Stack,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import type { RecentEventsContext } from '@/types/analysis';

export interface RecentEventsSectionProps {
  events: RecentEventsContext;
}

function sourceChipColor(
  source: string | null,
): 'primary' | 'warning' | 'error' | 'info' | 'default' {
  switch (source) {
    case 'github':
    case 'deploy':
      return 'primary';
    case 'kubernetes':
      return 'info';
    case 'watchdog':
      return 'warning';
    case 'alert':
      return 'error';
    default:
      return 'default';
  }
}

function formatWhenAgo(minutes: number | null): string {
  if (minutes == null) return '—';
  if (Math.abs(minutes) < 1) return 'just now';
  const sign = minutes >= 0 ? '' : 'in ';
  const abs = Math.abs(minutes);
  if (abs < 60) return `${sign}${Math.round(abs)}m ago`;
  const hours = abs / 60;
  if (hours < 24) return `${sign}${hours.toFixed(1)}h ago`;
  return `${sign}${Math.round(hours / 24)}d ago`;
}

export function RecentEventsSection({ events }: RecentEventsSectionProps) {
  if (events.events.length === 0) {
    return (
      <Box>
        <Typography variant="h6" sx={{ mb: 1 }}>
          What changed?
        </Typography>
        <Typography variant="body2" color="text.secondary">
          No deploys, k8s rollouts, or Watchdog stories in the {events.query} window.
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 1 }}>
        What changed?
      </Typography>
      <Stack spacing={1}>
        {events.events.map((evt, i) => (
          <Accordion
            key={`${evt.timestamp}-${i}`}
            disableGutters
            elevation={0}
            variant="outlined"
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 44 }}>
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                flexWrap="wrap"
                useFlexGap
                sx={{ width: '100%' }}
              >
                <Chip
                  size="small"
                  label={evt.source ?? 'event'}
                  color={sourceChipColor(evt.source)}
                  sx={{ height: 20, fontSize: 11 }}
                />
                <Typography variant="caption" color="text.secondary">
                  {formatWhenAgo(evt.when_ago_minutes)}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ flex: 1, minWidth: 0, fontWeight: 500 }}
                >
                  {evt.title ?? '(no title)'}
                </Typography>
                {evt.alert_type && (
                  <Chip
                    size="small"
                    label={evt.alert_type}
                    variant="outlined"
                    sx={{ height: 20, fontSize: 10 }}
                  />
                )}
              </Stack>
            </AccordionSummary>
            <AccordionDetails>
              <Box
                component="pre"
                sx={{
                  m: 0,
                  whiteSpace: 'pre-wrap',
                  fontFamily: 'monospace',
                  fontSize: 12,
                  lineHeight: 1.6,
                }}
              >
                {evt.text}
              </Box>
              {evt.tags.length > 0 && (
                <Stack
                  direction="row"
                  spacing={0.5}
                  flexWrap="wrap"
                  useFlexGap
                  sx={{ mt: 1 }}
                >
                  {evt.tags.slice(0, 12).map((tag) => (
                    <Chip
                      key={tag}
                      size="small"
                      label={tag}
                      variant="outlined"
                      sx={{ height: 18, fontSize: 10 }}
                    />
                  ))}
                </Stack>
              )}
            </AccordionDetails>
          </Accordion>
        ))}
      </Stack>
    </Box>
  );
}
